<template>
  <div>
    <!-- HEADER -->
    <div v-if="parentScreen !== 'Dashboard'">
      <div class="flex opacity-40 font-inter text-base font-semibold text-left text-stronger">
        Invest in Phoenix Energy
      </div>
      <div
        class="flex font-league text-[32px] font-semibold tracking-[-0.64] text-left text-strongest"
      >
        Select the bond offering for you
      </div>
      <div class="font-inter text-lg font-medium text-left text-strong">
        Get started with as little as $1,000
      </div>
    </div>
    <!-- OFFERING CARD CONTAINER -->
    <div class="flex flex-col lg:flex-row pt-[12px] mt-[32px] gap-4 w-full">
      <OfferingCardV3
        v-for="offering in offeringCards"
        :maxRateByTermLength="maxRateByTermLength"
        :maxDetailsLength="maxDetailsLength"
        :key="offering.title"
        :offering="offering"
        :parent-screen="props.parentScreen"
        class="flex-1 min-w-0 order-[var(--mobile-order)] lg:order-[var(--desktop-order)]"
        :style="{
          '--mobile-order': offering.mobile_order,
          '--desktop-order': offering.desktop_order,
        }"
      />
    </div>
    <!-- Want to learn more? -->
    <div class="w-full justify-center flex mt-[28px] items-center">
      <Icon name="BadgeDollarSign" class="h-5 w-5 text-green-500 mr-2" />
      <div class="text-lg text-strongest mr-2">
        Want to learn about our highest available rates for accredited investors?
      </div>
      <div
        class="text-lg text-paper-8 dark:text-paper-2 underline cursor-pointer mr-1"
        @click="router.push('/contact-us')"
      >
        Contact us to learn more
      </div>
      <Icon name="ArrowUpRight" class="w-4 h-4 text-paper-8 dark:text-paper-2" :stroke-width="3" />
    </div>

    <!-- EXTRA LINKS -->
    <div
      class="mt-[28px] mb-[64px] flex flex-wrap md:flex-nowrap gap-2 w-full md:justify-center"
      v-if="parentScreen !== 'Dashboard'"
    >
      <div
        v-for="link of extraLinks"
        class="p-3 flex bg-white border border-radio-checkbox-outline rounded-full items-center dark:bg-primary-7 text-strongest cursor-pointer transition-colors duration-300 ease-in-out hover:bg-filter-bg-hover dark:hover:bg-primary-7/70"
        @click="redirect(link)"
      >
        <div class="flex items-center" v-if="link.icon">
          <Icon :name="link.icon" class="w-4 h-4 text-outline-dark" :stroke-width="3" />
        </div>
        <div class="flex items-center" v-if="link.img">
          <img :src="link.img" alt="" class="w-4 h-4 object-contain" />
          <div
            v-if="link.type == 'trust_pilot_reviews'"
            class="ml-2 font-inter text-success font-bold"
          >
            4.7
          </div>
          <div v-if="link.type == 'bbb_profile'" class="ml-2 font-inter text-[#006495] font-bold">
            A+
          </div>
        </div>

        <div class="mx-2 flex items-center font-inter leading-none">{{ link.title }}</div>
        <div class="flex items-center">
          <Icon name="ArrowUpRight" class="w-4 h-4 text-outline-dark" :stroke-width="3" />
        </div>
      </div>
    </div>

    <!-- DISCLOSURE -->
  </div>
</template>

<script setup>
import { computed } from "vue";

import OfferingCardV3 from "./OfferingCardV3.vue";

import offeringIcon_Adamantium from "@/assets/images/investment_diamond.png";
import offeringIcon_Reg_D from "@/assets/images/investment_gold_circle.png";
import bbb from "@/assets/images/bbb.png";
import trustpilot from "@/assets/images/trustpilot.png";
import offeringIcon_S1 from "@/assets/images/investment_silver_circle.png";
import { useDataStore, useUIStore, usePurchaseFlowStore, useCrudStore } from "@/stores";
import { useAnalytics } from "@/composables/useAnalytics";
import { Button, Icon } from "@/components";
import { useRouter } from "vue-router";

const dataStore = useDataStore();
const UIStore = useUIStore();
const purchaseFlowStore = usePurchaseFlowStore();
const crudStore = useCrudStore();
const { trackOfferingCtaClicked } = useAnalytics();

const router = useRouter();

const props = defineProps({
  parentScreen: {
    type: String,
    required: false,
  },
});

const extraLinks = computed(() => [
  {
    title: "SEC Edgar Filings",
    link: "https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=0001818643&owner=include&count=40",
    type: "sec_edgar_filings",
    icon: "FileText",
  },
  {
    title: "Annual Report",
    link: "https://www.sec.gov/Archives/edgar/data/1818643/000119312526110825/0001193125-26-110825-index.htm",
    type: "annual_report",
    icon: "LineChart",
  },
  {
    title: "Trustpilot Reviews",
    link: "https://www.trustpilot.com/review/phoenixenergy.com",
    type: "trust_pilot_reviews",
    img: trustpilot,
  },
  {
    title: "BBB Profile",
    link: "https://www.bbb.org/us/co/denver/profile/mineral-exploration/phoenix-energy-one-llc-1296-1000116466/customer-reviews?utm_source=googleads&utm_medium=cpc&utm_campaign={newBBBYT}&gad_source=1&gad_campaignid=22690963153&gbraid=0AAAAA_OBlYRgbw_I9HJU27Z7ud9hTo8A8",
    type: "bbb_profile",
    img: bbb,
  },
]);

function getRateInfo(
  available_offerings,
  rangePredicate,
  termPredicate = rangePredicate,
  includeTier = false
) {
  // Computes the "X-Y%" rate range and the per-term rate breakdown for a set of offerings.
  // rangePredicate/termPredicate can differ because the Reg D card intentionally reports the
  // full tier's rate range while only breaking out rate_by_term for tiers below 2.
  const rangeMatches = available_offerings.filter(rangePredicate);
  const rates = rangeMatches.map((o) => o.interest_rate);
  const rate_range = `${Math.min(...rates)}-${Math.max(...rates)}%`;

  const rate_by_term = available_offerings
    .filter(termPredicate)
    .map((o) =>
      includeTier
        ? { term: o.bond_term, rate: o.interest_rate, tier: o.tier }
        : { term: o.bond_term, rate: o.interest_rate }
    )
    .sort((a, b) => b.rate - a.rate);

  return { rate_range, rate_by_term };
}

const offeringCards = computed(() => {
  let cards = [];
  let available_offerings = dataStore.offerings.filter(
    (offering) => offering.is_visible_to_investors
  );

  const show_short_term_s1 = available_offerings.some(
    (offering) => offering.is_short_term && offering.type === "S1"
  );
  const show_regular_s1 = available_offerings.some(
    (offering) => !offering.is_short_term && offering.type === "S1"
  );
  const show_regular_reg_d = available_offerings.some(
    (offering) => !offering.is_short_term && offering.type === "Reg D"
  );

  if (show_short_term_s1) {
    const { rate_range, rate_by_term } = getRateInfo(
      available_offerings,
      (o) => o.is_short_term && o.type === "S1"
    );

    cards.push({
      title: "Phoenix Flex Notes",
      sub_title: "Flexible Note Offering",
      sub_title_footnote_ref: 1,
      minimum_investment: 1000,
      rate_range,
      open_to: "all",
      icon_name: "Briefcase",
      rate_by_term,
      details: [
        {
          text: "Open to all investors",
          footnote_ref: 3,
        },
        {
          text: "Monthly payments or compounding interest",
        },
        {
          text: "Set redemption intervals",
          footnote_ref: 2,
        },
        {
          text: "IRA eligible",
        },
      ],
      link_title: "Prospectus",
      link: "https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&filenum=333-282862",
      is_short_term: true,
      type: "S1",
      desktop_order: 1,
      mobile_order: 2,
    });
  }

  if (show_regular_s1) {
    const { rate_range, rate_by_term } = getRateInfo(
      available_offerings,
      (o) => !o.is_short_term && o.type === "S1"
    );

    cards.push({
      title: "Registered Offering",
      sub_title: "Long Term Bond Offering",
      sub_title_footnote_ref: 0,
      minimum_investment: 5000,
      rate_range,
      open_to: "all",
      icon_name: "Briefcase",
      rate_by_term,
      details: [
        {
          text: "Open to all investors",
          footnote_ref: 3,
        },
        {
          text: "Monthly payments or compounding interest",
        },
        {
          text: "IRA eligible",
        },
      ],
      link_title: "Prospectus",
      link: "https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&filenum=333-282862",
      is_short_term: false,
      is_most_popular: true,
      type: "S1",
      desktop_order: 2,
      mobile_order: 1,
    });
  }
  if (show_regular_reg_d) {
    // rate_range and rate_by_term are both scoped to the investor's currently-qualifying
    // regDTier, so every rate in the headline range is actually present in the table below.
    const { rate_range, rate_by_term } = getRateInfo(
      available_offerings,
      (o) => !o.is_short_term && o.type === "Reg D" && o.tier === purchaseFlowStore.regDTier,
      undefined,
      true
    );

    cards.push({
      title: "Private Placement",
      sub_title: "Regulation D Offering",
      sub_title_footnote_ref: 0,
      minimum_investment: 25000,
      rate_range,
      open_to: "accredited",
      icon_name: "Shield",
      rate_by_term,
      has_rate_lift: purchaseFlowStore.regDTier > 1,
      details: [
        {
          text: "Open to accredited investors",
        },
        {
          text: "Monthly payments or compounding interest",
        },
        {
          text: "IRA eligible",
        },
        {
          text: "Rate lifts available",
        },
      ],
      link_title: "Private Placement Memorandum",
      link: "https://phoenixenergy.com/ppm/?_gl=1*93svuu*_gcl_au*MTg4NTkxMjM0OS4xNzcxNTQ2OTI4",
      is_short_term: false,

      type: "Reg D",
      desktop_order: 3,
      mobile_order: 3,
    });
  }

  return cards;
});

const maxRateByTermLength = computed(() => {
  return Math.max(...offeringCards.value.map((offering) => offering.rate_by_term.length));
});

const maxDetailsLength = computed(() => {
  return Math.max(...offeringCards.value.map((offering) => offering.details.length));
});

function redirect(link) {
  trackOfferingCtaClicked(link.title, null, link.type);
  let log = {
    step: "Experiment CTA",
    substep: link.title,
    status: "Navigated",
    message: "",
    url: link.link,
    validation_details: null,
  };
  log = dataStore.setDefaultFields(log, "investoractivity");
  crudStore.insert("investoractivity", [log]);
  window.open(link.link, "_blank");
}
</script>
<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none; /* IE & Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
